/** RFC 8628 device-code flow against the Verentis Security OIDC endpoints. */

export const CLI_CLIENT_ID = 'Verentis.Cli'

export interface DeviceAuthorization {
  device_code: string
  user_code: string
  verification_uri: string
  verification_uri_complete?: string
  expires_in: number
  interval: number
}

interface TokenResponse {
  id_token?: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  error?: string | null
}

export interface IdentityResult {
  idToken: string
  refreshToken?: string
}

export async function startDeviceAuthorization(apiUrl: string): Promise<DeviceAuthorization> {
  const res = await fetch(`${apiUrl}/connect/device/authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLI_CLIENT_ID }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Could not start device sign-in (${res.status}).${text ? ` ${text.slice(0, 300)}` : ''}`)
  }
  return (await res.json()) as DeviceAuthorization
}

/** Polls the token endpoint until approval, denial or expiry. Returns the identity + refresh tokens. */
export async function pollForIdentityToken(
  apiUrl: string,
  authorization: DeviceAuthorization,
  onPoll?: () => void,
): Promise<IdentityResult> {
  let intervalMs = Math.max(authorization.interval, 1) * 1000
  const deadline = Date.now() + authorization.expires_in * 1000

  while (Date.now() < deadline) {
    await sleep(intervalMs)
    onPoll?.()

    const res = await fetch(`${apiUrl}/connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'device_code',
        device_code: authorization.device_code,
        client_id: CLI_CLIENT_ID,
      }),
    })

    let payload: TokenResponse | undefined
    try {
      payload = (await res.json()) as TokenResponse
    } catch {
      payload = undefined
    }

    if (res.ok && payload?.id_token) {
      return { idToken: payload.id_token, refreshToken: payload.refresh_token }
    }

    switch (payload?.error) {
      case 'authorization_pending':
        continue
      case 'slow_down':
        intervalMs += 5000
        continue
      case 'access_denied':
        throw new Error('Sign-in was denied in the browser.')
      case 'expired_token':
        throw new Error('The device code expired before it was approved. Run `verentis login` again.')
      default:
        throw new Error(`Device sign-in failed (${res.status}).${payload?.error ? ` ${payload.error}` : ''}`)
    }
  }

  throw new Error('The device code expired before it was approved. Run `verentis login` again.')
}

/** RefreshTokenError signals the refresh token is no longer usable — the caller must re-run `verentis login`. */
export class RefreshTokenError extends Error {}

/**
 * Exchanges a refresh token for a fresh identity token (RFC 6749 §6), rotating the refresh token.
 * Throws {@link RefreshTokenError} when the token is invalid/expired/revoked so callers can prompt a re-login.
 */
export async function refreshIdentityToken(apiUrl: string, refreshToken: string): Promise<IdentityResult> {
  const res = await fetch(`${apiUrl}/connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: CLI_CLIENT_ID,
    }),
  })

  let payload: TokenResponse | undefined
  try {
    payload = (await res.json()) as TokenResponse
  } catch {
    payload = undefined
  }

  if (res.ok && payload?.id_token) {
    return { idToken: payload.id_token, refreshToken: payload.refresh_token ?? refreshToken }
  }

  // RFC 6749 §5.2: invalid_grant / invalid_client mean the token can't be renewed — re-login required.
  throw new RefreshTokenError(
    `Session could not be renewed${payload?.error ? ` (${payload.error})` : ` (${res.status})`}. Run \`verentis login\` again.`,
  )
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
